/* ==========================================
   HumanAlloy Card Scale Controls (V7)
   - HARDENED: survives other errors, logs status
   - MutationObserver: re-applies scale when CloudSearch replaces grid
   - 🖼️ Updates image sizes attr so browser picks optimal srcset
   - Sets window.__HA_CARD_SCALE_LOADED__ = true
   ========================================== */

(function () {
  try {
    // Skip search pages — scale widget not needed there
    if (location.pathname === "/search" || location.pathname === "/a/search") {
      console.log("🧾 HA CardScale: skipped (search page) 🔍");
      return;
    }

    // Flag so we can confirm execution from console
    window.__HA_CARD_SCALE_LOADED__ = true;

    const CONFIG = {
      scopeClass: "ha-cardscale-scope",
      hideLabelsClass: "ha-hide-labels",
      storageKeyScale: "ha_card_scale",
      storageKeyHide: "ha_hide_labels",
      min: 0.10,
      max: 2.50,
      step: 0.05,
      defaultScale: 1.00,
      autoHideThreshold: 0.55,
      defaultHideLabels: false,
      autoHideEnabled: true,
      retryEveryMs: 250,
      maxRetries: 24
    };

    const doc = document;
    const root = doc.documentElement;

    const clamp = (v, a, b) => Math.min(b, Math.max(a, v));

    function log(...args) {
      console.log("🧾 HA CardScale:", ...args);
    }

    function getState() {
      let scale = parseFloat(localStorage.getItem(CONFIG.storageKeyScale));
      if (!Number.isFinite(scale)) scale = CONFIG.defaultScale;

      let hideLabels = localStorage.getItem(CONFIG.storageKeyHide);
      hideLabels = hideLabels === null ? CONFIG.defaultHideLabels : (hideLabels === "true");

      return { scale, hideLabels };
    }

    // ========================
    // 🖼️ Update image sizes for optimal srcset selection
    // ========================

    let _imgSizesRaf = 0;

    function updateImageSizes() {
      const grid = doc.querySelector("#product-grid");
      if (!grid) return;

      // Get actual rendered width of first card
      const firstCard = grid.querySelector("li.grid__item");
      if (!firstCard) return;
      const cardWidth = Math.round(firstCard.offsetWidth);
      if (cardWidth < 10) return; // not rendered yet

      // Update all images in grid
      const newSizes = cardWidth + "px";
      grid.querySelectorAll("img[srcset]").forEach(img => {
        img.sizes = newSizes;
      });

      log("Image sizes →", cardWidth + "px 🖼️");
    }

    // Debounced version for use during slider drag
    function scheduleImageSizesUpdate() {
      if (_imgSizesRaf) cancelAnimationFrame(_imgSizesRaf);
      _imgSizesRaf = requestAnimationFrame(() => {
        updateImageSizes();
      });
    }

    // ========================

    function applyState(scale, hideLabels) {
      const body = doc.body;
      if (!body) return;

      body.classList.add(CONFIG.scopeClass);

      scale = clamp(scale, CONFIG.min, CONFIG.max);

      root.style.setProperty("--ha-card-scale", String(scale));
      localStorage.setItem(CONFIG.storageKeyScale, String(scale));

      if (CONFIG.autoHideEnabled) {
        const autoHide = scale <= CONFIG.autoHideThreshold;
        body.classList.toggle(CONFIG.hideLabelsClass, hideLabels || autoHide);
      } else {
        body.classList.toggle(CONFIG.hideLabelsClass, hideLabels);
      }

      const valEl = doc.querySelector("#haCardScaleValue");
      if (valEl) valEl.textContent = `${Math.round(scale * 100)}%`;
    }

    // 🔄 Re-apply current scale state (called by observer when grid changes)
    function reapplyCurrentState() {
      const { scale, hideLabels } = getState();
      applyState(scale, hideLabels);
      scheduleImageSizesUpdate();
    }

    function alreadyBound(el) {
      return el && el.dataset && el.dataset.haBound === "1";
    }

    function bindUI() {
      const slider = doc.querySelector("#haCardScaleSlider");
      if (!slider) return false;

      const { scale, hideLabels } = getState();
      applyState(scale, hideLabels);

      // Initial image sizes update after layout settles
      setTimeout(updateImageSizes, 300);

      if (alreadyBound(slider)) return true;
      slider.dataset.haBound = "1";

      const btnSmaller = doc.querySelector("#haCardScaleSmaller");
      const btnBigger  = doc.querySelector("#haCardScaleBigger");
      const btnReset   = doc.querySelector("#haCardScaleReset");
      const hideToggle = doc.querySelector("#haHideLabelsToggle");

      slider.min = CONFIG.min;
      slider.max = CONFIG.max;
      slider.step = CONFIG.step;
      slider.value = scale;

      let raf = 0;

      function setScale(v) {
        const state = getState();
        const newScale = clamp(v, CONFIG.min, CONFIG.max);
        localStorage.setItem(CONFIG.storageKeyScale, String(newScale));
        applyState(newScale, state.hideLabels);
      }

      function setHide(v) {
        const state = getState();
        localStorage.setItem(CONFIG.storageKeyHide, String(!!v));
        applyState(state.scale, !!v);
        if (hideToggle) hideToggle.checked = !!v;
      }

      // 🎚️ Slider: update scale live, update image sizes on release
      slider.addEventListener("input", (e) => {
        const v = parseFloat(e.target.value);
        if (raf) cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => setScale(v));
      });

      slider.addEventListener("change", () => {
        // Slider released → update image sizes to match new card width
        updateImageSizes();
      });

      if (btnSmaller) btnSmaller.onclick = () => {
        const cur = parseFloat(localStorage.getItem(CONFIG.storageKeyScale)) || CONFIG.defaultScale;
        const v = clamp(cur - CONFIG.step, CONFIG.min, CONFIG.max);
        slider.value = v;
        setScale(v);
        updateImageSizes();
      };

      if (btnBigger) btnBigger.onclick = () => {
        const cur = parseFloat(localStorage.getItem(CONFIG.storageKeyScale)) || CONFIG.defaultScale;
        const v = clamp(cur + CONFIG.step, CONFIG.min, CONFIG.max);
        slider.value = v;
        setScale(v);
        updateImageSizes();
      };

      if (btnReset) btnReset.onclick = () => {
        slider.value = CONFIG.defaultScale;
        setHide(CONFIG.defaultHideLabels);
        setScale(CONFIG.defaultScale);
        updateImageSizes();
      };

      if (hideToggle) {
        hideToggle.checked = hideLabels;
        hideToggle.onchange = (e) => setHide(e.target.checked);
      }

      log("Bound UI ✅", { scale, hideLabels });
      return true;
    }

    let retryTimer = null;

    function initWithRetries() {
      if (retryTimer) clearInterval(retryTimer);

      let tries = 0;
      retryTimer = setInterval(() => {
        tries++;
        const ok = bindUI();
        if (ok || tries >= CONFIG.maxRetries) {
          clearInterval(retryTimer);
          retryTimer = null;
          if (!ok) log("Widget not found after retries ❌ (check HTML IDs / placement)");
        }
      }, CONFIG.retryEveryMs);
    }

    // ========================
    // 🔭 Grid MutationObserver
    // Re-applies card scale when CloudSearch replaces grid content
    // ========================

    let _gridObserver = null;
    let _gridReapplyRaf = 0;

    function observeGrid() {
      if (_gridObserver) return; // already watching

      const grid = doc.querySelector("#product-grid");
      const target = grid ? grid.parentElement || grid : null;
      if (!target) return;

      _gridObserver = new MutationObserver(() => {
        if (_gridReapplyRaf) cancelAnimationFrame(_gridReapplyRaf);
        _gridReapplyRaf = requestAnimationFrame(() => {
          reapplyCurrentState();
          log("Grid changed → re-applied scale 🔄");
        });
      });

      _gridObserver.observe(target, { childList: true, subtree: true });
      log("Grid observer started 🔭");
    }

    function boot() {
      initWithRetries();
      observeGrid();
    }

    if (doc.readyState === "loading") {
      doc.addEventListener("DOMContentLoaded", boot);
    } else {
      boot();
    }

    doc.addEventListener("shopify:section:load", boot);
    doc.addEventListener("shopify:section:reorder", boot);
    window.addEventListener("popstate", boot);

    log("Script loaded ✅");
  } catch (err) {
    console.error("🧾 HA CardScale FATAL:", err);
  }
})();

window.__CUSTOM_JS_RAN_TO_END__ = true;
console.log("🧾 ha-card-scale.js ran to end ✅");